import { useState } from "react";
import { approveDemoBrief } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X, Phone, MapPin, ExternalLink, Instagram, Facebook, Mail, User } from "lucide-react";

// Gate between "brief generated" and "full site built" — the expensive HTML generation
// (up to 16k output tokens) only runs once a human has reviewed the brief and, ideally,
// checked it against whatever was actually agreed on a call with the business. Editing a
// bullet here is free; regenerating the whole site after a bad brief is not.
export default function BriefReviewModal({ demo, businessName, contact, onApproved, onClose }) {
  const initial = (demo?.blueprint?.brief || []).slice(0, 4);
  while (initial.length < 4) initial.push("");
  const [lines, setLines] = useState(initial);
  const [approving, setApproving] = useState(false);
  const [comment, setComment] = useState(contact?.notes || "");

  const setLine = (i, value) => {
    setLines(prev => prev.map((l, idx) => idx === i ? value : l));
  };

  const handleApprove = async () => {
    const brief = lines.map(l => l.trim()).filter(Boolean);
    if (brief.length === 0) { toast.error("At least one brief line is required"); return; }
    setApproving(true);
    try {
      const built = await approveDemoBrief(demo.id, brief);
      toast.success("Brief approved — site built");
      onApproved(built, comment.trim());
    } catch (e) {
      toast.error("Build failed: " + (e?.response?.data?.detail || e?.message || "unknown"));
    } finally {
      setApproving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
      onClick={() => !approving && onClose()}
    >
      <div
        className="max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: "#ffffff", border: "1px solid #e5e7eb" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] mb-2" style={{ color: "#FF3B30" }}>REVIEW THE BRIEF</p>
            <h3 className="font-display text-3xl tracking-tighter">{businessName}</h3>
          </div>
          {!approving && (
            <button onClick={onClose} style={{ color: "#9ca3af" }}>
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <p className="text-sm mb-4" style={{ color: "#6b7280" }}>
          This is what gets pitched — and what the full site build will implement. Edit it against
          anything actually agreed on a call before building; the site build is expensive to redo.
        </p>

        {contact && (
          <div
            className="mb-6 p-4 flex flex-col gap-2 font-mono text-[13px]"
            style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb" }}
          >
            {contact.phone && (
              <a href={`tel:${contact.phone}`} className="inline-flex items-center gap-2 hover:text-[#FF3B30]" style={{ color: "#111827" }}>
                <Phone className="w-3.5 h-3.5" style={{ color: "#9ca3af" }} /> {contact.phone}
              </a>
            )}
            {contact.contact_name && (
              <span className="inline-flex items-center gap-2" style={{ color: "#374151" }}>
                <User className="w-3.5 h-3.5" style={{ color: "#9ca3af" }} /> {contact.contact_name}
              </span>
            )}
            {contact.contact_email && (
              <a href={`mailto:${contact.contact_email}`} className="inline-flex items-center gap-2 hover:text-[#FF3B30]" style={{ color: "#374151" }}>
                <Mail className="w-3.5 h-3.5" style={{ color: "#9ca3af" }} /> {contact.contact_email}
              </a>
            )}
            {contact.address && (
              <span className="inline-flex items-center gap-2" style={{ color: "#374151" }}>
                <MapPin className="w-3.5 h-3.5" style={{ color: "#9ca3af" }} /> {contact.address}
              </span>
            )}
            {contact.website && (
              <a href={contact.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-[#FF3B30]" style={{ color: "#374151" }}>
                <ExternalLink className="w-3.5 h-3.5" style={{ color: "#9ca3af" }} /> {contact.website}
              </a>
            )}
            {contact.instagram && (
              <a href={contact.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-[#FF3B30]" style={{ color: "#374151" }}>
                <Instagram className="w-3.5 h-3.5" style={{ color: "#9ca3af" }} /> {contact.instagram}
              </a>
            )}
            {contact.facebook && (
              <a href={contact.facebook} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-[#FF3B30]" style={{ color: "#374151" }}>
                <Facebook className="w-3.5 h-3.5" style={{ color: "#9ca3af" }} /> {contact.facebook}
              </a>
            )}
            {contact.rating != null && (
              <span className="text-xs" style={{ color: "#9ca3af" }}>
                {contact.rating}★ · {contact.reviews ?? 0} reviews
              </span>
            )}
          </div>
        )}

        <div className="mb-6">
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] block mb-1.5" style={{ color: "#9ca3af" }}>
            Comment — saved to the CRM notes
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            placeholder="Anything from the call worth remembering — objections, timing, who to ask for…"
            className="w-full px-3 py-2.5 text-sm outline-none resize-none"
            style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }}
          />
        </div>

        <div className="space-y-4">
          {lines.map((line, i) => (
            <div key={i}>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] block mb-1.5" style={{ color: "#9ca3af" }}>
                {String(i + 1).padStart(2, "0")}
              </label>
              <textarea
                value={line}
                onChange={(e) => setLine(i, e.target.value)}
                rows={2}
                maxLength={140}
                className="w-full px-3 py-2.5 text-sm outline-none resize-none"
                style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", color: "#111827" }}
              />
            </div>
          ))}
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={approving}
            className="px-5 py-3 font-display transition-colors disabled:opacity-40"
            style={{ border: "1px solid #e5e7eb", color: "#374151" }}
          >
            Not yet
          </button>
          <button
            onClick={handleApprove}
            disabled={approving}
            className="bg-[#FF3B30] text-white px-6 py-3 font-display font-bold hover:bg-[#E6352B] disabled:opacity-50 transition-colors inline-flex items-center gap-2"
          >
            {approving ? <><Loader2 className="w-4 h-4 animate-spin" /> Building…</> : "Approve & build site →"}
          </button>
        </div>
      </div>
    </div>
  );
}
